import { canonicalizeRecordKeys } from "../validate/fieldMap";
/**
 * Mapping a customer's spreadsheet headers onto the canonical column vocabulary.
 *
 * Three passes, most confident first, and the first pass that matches wins:
 *
 *  1. exact canonical name. A file exported from a Tab Service template lands here.
 *  2. a small alias table for the headers people actually type: "Recipient TIN",
 *     "Payer EIN". It runs before the shape match so an alias outranks a coincidence of
 *     spelling with some other column.
 *  3. shape-insensitive. "Recipient First Name", "recipient_first_name" and
 *     "RECIPIENTFIRSTNAME" all reduce to the same key, so case, spaces, hyphens and
 *     underscores stop mattering. Box aliases ("Box 1") resolve against the real column
 *     list rather than being hard-coded, because the box names differ per form.
 *
 * A header that matches nothing is left unmapped and reported, never guessed at. Guessing
 * a column wrong on a tax return is worse than telling the customer to name it properly.
 */

/** Reduces a header to its comparable shape: letters and digits, upper case. */
export function normalizeHeader(header: string): string {
  return header.replace(/[^A-Za-z0-9]+/g, "").toUpperCase();
}

/** Common headers to the canonical names they mean, in priority order. */
const ALIASES: Record<string, string[]> = {
  RECORDREF: ["recordRef", "RECORD_REF"],
  REFERENCE: ["recordRef", "RECORD_REF"],
  RECORDID: ["recordRef", "RECORD_REF"],

  TIN: ["RECIPIENT_ID_NUMBER", "RECIPIENT_TIN"],
  RECIPIENTTIN: ["RECIPIENT_ID_NUMBER", "RECIPIENT_TIN"],
  RECIPIENTSSN: ["RECIPIENT_ID_NUMBER"],
  RECIPIENTEIN: ["RECIPIENT_ID_NUMBER"],
  RECIPIENTIDNUMBER: ["RECIPIENT_ID_NUMBER"],
  SSN: ["RECIPIENT_ID_NUMBER"],
  EMPLOYEETIN: ["EMPLOYEE_ID_NUMBER", "RECIPIENT_ID_NUMBER"],

  PAYEREIN: ["FILER_FEDERAL_ID_NUMBER", "PAYER_FEDERAL_ID_NUMBER"],
  PAYERTIN: ["FILER_FEDERAL_ID_NUMBER", "PAYER_FEDERAL_ID_NUMBER"],
  FILEREIN: ["FILER_FEDERAL_ID_NUMBER"],
  FILERTIN: ["FILER_FEDERAL_ID_NUMBER"],
  EIN: ["FILER_FEDERAL_ID_NUMBER"],

  PAYERNAME: ["FILER_NAME1", "PAYER_NAME1", "FILER_NAME"],
  FILERNAME: ["FILER_NAME1", "FILER_NAME"],
  ISSUERNAME: ["FILER_NAME1", "ISSUER_NAME1"],

  // A plain "Recipient Name" column is the combined name, person or business - not a business
  // name. Mapped to RECIPIENT_BUSINESS_NAME1 it made every recipient a business, SSNs included.
  // The combined name is classified and split by intake/nameIdentification.
  RECIPIENTNAME: ["RECIPIENT_NAME", "RECIPIENT_NAME1", "RECIPIENT_BUSINESS_NAME1"],
  // "Recipient Full Name" says the same thing. Left unmapped, a file that carried only this
  // column filed every recipient with no name (TS-8768).
  RECIPIENTFULLNAME: ["RECIPIENT_NAME", "RECIPIENT_NAME1", "RECIPIENT_BUSINESS_NAME1"],
  FULLNAME: ["RECIPIENT_NAME", "RECIPIENT_NAME1", "RECIPIENT_BUSINESS_NAME1"],
  BUSINESSNAME: ["RECIPIENT_BUSINESS_NAME1"],
  FIRSTNAME: ["RECIPIENT_FIRST_NAME"],
  MIDDLENAME: ["RECIPIENT_MIDDLE_NAME"],
  MIDDLEINITIAL: ["RECIPIENT_MIDDLE_NAME"],
  LASTNAME: ["RECIPIENT_LAST_NAME"],
  SUFFIX: ["RECIPIENT_SUFFIX"],

  ADDRESS: ["RECIPIENT_ADDRESS1", "RECIPIENT_ADDRESS"],
  ADDRESS1: ["RECIPIENT_ADDRESS1", "RECIPIENT_ADDRESS"],
  ADDRESS2: ["RECIPIENT_ADDRESS2"],
  STREET: ["RECIPIENT_ADDRESS1"],
  APT: ["RECIPIENT_APT"],
  SUITE: ["RECIPIENT_SUITE"],
  CITY: ["RECIPIENT_CITY"],
  STATE: ["RECIPIENT_STATE"],
  ZIP: ["RECIPIENT_ZIP_CODE", "RECIPIENT_ZIP"],
  ZIPCODE: ["RECIPIENT_ZIP_CODE", "RECIPIENT_ZIP"],
  POSTALCODE: ["RECIPIENT_ZIP_CODE", "RECIPIENT_ZIP"],
  COUNTRY: ["RECIPIENT_COUNTRY"],

  ACCOUNT: ["ACCOUNT_NUMBER"],
  ACCOUNTNUMBER: ["ACCOUNT_NUMBER"],
  EMAIL: ["RECIPIENT_EMAIL", "EMAIL_ADDRESS"],
  PHONE: ["RECIPIENT_PHONE", "PHONE_NUMBER"],

  // Pub 1220 vocabulary, for a spreadsheet exported from a FIRE-shaped system.
  PAYEETIN: ["RECIPIENT_ID_NUMBER", "RECIPIENT_TIN"],
  PAYEENAME: ["RECIPIENT_NAME", "RECIPIENT_NAME1", "RECIPIENT_BUSINESS_NAME1"],
  PAYEENAMELINE1: ["RECIPIENT_NAME", "RECIPIENT_NAME1", "RECIPIENT_BUSINESS_NAME1"],
  PAYEENAMELINE2: ["RECIPIENT_BUSINESS_NAME2", "RECIPIENT_NAME2"],
  PAYEEMAILINGADDRESS: ["RECIPIENT_ADDRESS1"],
  PAYEECITY: ["RECIPIENT_CITY"],
  PAYEESTATE: ["RECIPIENT_STATE"],
  PAYEEZIPCODE: ["RECIPIENT_ZIP_CODE", "RECIPIENT_ZIP"],
  PAYERNAMELINE1: ["FILER_NAME1", "PAYER_NAME1"],
  PAYERNAMELINE2: ["FILER_NAME2", "PAYER_NAME2"],
  PAYERSHIPPINGADDRESS: ["FILER_ADDRESS1"],
  PAYERCITY: ["FILER_CITY"],
  PAYERSTATE: ["FILER_STATE"],
  // Both spellings of the filer's ZIP: the forms are not consistent with each other, and a
  // candidate that this form does not have is skipped.
  PAYERZIPCODE: ["FILER_ZIP_CODE", "FILER_ZIP"],

  // What a payer's own columns are called outside the Pub 1220 vocabulary. Every 1099 names
  // this party the payer on the paper form and the filer in the column list, and a file that
  // says "Payer Address" was landing nowhere: the address and the ZIP of the payer were
  // dropped from the record without a word, which is how a demo file ended up filing returns
  // with no payer address on them.
  PAYERADDRESS: ["FILER_ADDRESS1"],
  PAYERADDRESS1: ["FILER_ADDRESS1"],
  PAYERADDRESS2: ["FILER_ADDRESS2"],
  PAYERSTREET: ["FILER_ADDRESS1"],
  PAYERSTREETADDRESS: ["FILER_ADDRESS1"],
  PAYERZIP: ["FILER_ZIP_CODE", "FILER_ZIP"],
  PAYERPOSTALCODE: ["FILER_ZIP_CODE", "FILER_ZIP"],
  PAYERCOUNTRY: ["FILER_COUNTRY"],
  PAYERPHONE: ["FILER_PHONE", "FILER_TELEPHONE"],
  // The payer's second name line and suite, as a file outside the Pub 1220 vocabulary writes
  // them. "Payer Name" mapped and "Payer Name 2" did not, so the second line was dropped.
  PAYERNAME2: ["FILER_NAME2", "PAYER_NAME2"],
  PAYERSUITENO: ["FILER_SUITE_NO"],
  PAYERSUITE: ["FILER_SUITE_NO"],
  FILERADDRESS: ["FILER_ADDRESS1"],
  FILERZIP: ["FILER_ZIP", "FILER_ZIP_CODE"],
  FILERZIPCODE: ["FILER_ZIP", "FILER_ZIP_CODE"],

  RECIPIENTBUSINESSNAME: ["RECIPIENT_BUSINESS_NAME1", "RECIPIENT_NAME1", "RECIPIENT_NAME"],
  PAYEEBUSINESSNAME: ["RECIPIENT_BUSINESS_NAME1", "RECIPIENT_NAME1", "RECIPIENT_NAME"],

  // The 2026 templates renamed two 1099-MISC columns without changing what they hold. Both
  // spellings mean the one column, so a file written to either template maps.
  BOX12SECTION409ADEFERRALS: ["BOX12_SECTION_409A_DEFERRAL"],
  BOX13BTTOC: ["BOX13B_TTOC1"],

  FEDERALTAXWITHHELD: ["FEDERAL_INCOME_TAX_WITHHELD"],
  FEDERALINCOMETAXWITHHELD: ["FEDERAL_INCOME_TAX_WITHHELD"],
  STATETAXWITHHELD: ["STATE_INCOME_TAX_WITHHELD", "STATE_TAX_WITHHELD"],
};

/**
 * Pub 1220 amount codes to the box number they print in, per form.
 *
 * A FIRE file numbers its money fields by amount code, not by box, and the two only
 * sometimes agree: on 1099-MISC, amount code A is box 10. A spreadsheet exported from such
 * a system carries headers like "Payment Amount A", so the code has to be translated before
 * the box lookup can run. Kept deliberately small: 1099-NEC and 1099-MISC in full, which is
 * what the POC files. Any other form falls back to plain box matching.
 */
export const AMOUNT_CODE_BOX: Record<string, Record<string, string>> = {
  "1099-NEC": {
    "1": "1",
    "4": "4",
  },
  "1099-MISC": {
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
    "5": "5",
    "6": "6",
    "8": "8",
    "9": "9",
    A: "10",
    B: "11",
    C: "12",
    D: "14",
    E: "15",
  },
};

/**
 * Finds the canonical column for a box number.
 *
 * The boundary check is what keeps box 1 from matching BOX10_GROSS_PROCEEDS: after the
 * digits, the next character must not be another digit. A trailing letter is fine, because
 * BOX1A is genuinely box 1.
 */
function canonicalForBox(
  box: string,
  canonicalNames: string[],
  claimed: Set<string>
): string | null {
  const prefix = `BOX${box.toUpperCase()}`;
  const exact = canonicalNames.find((name) => {
    if (claimed.has(name)) return false;
    const shape = normalizeHeader(name);
    if (!shape.startsWith(prefix)) return false;
    const next = shape.charAt(prefix.length);
    return next === "" || !/[0-9]/.test(next);
  });
  return exact ?? null;
}

export interface MappingResult {
  /** Source header -> canonical column name. */
  mapping: Record<string, string>;
  /** Headers that matched nothing. Reported to the customer, never guessed. */
  unmapped: string[];
}

/**
 * Works out which canonical column each header means.
 *
 * `canonicalNames` comes from the engine's JSON Schema for the form, so the vocabulary
 * follows the tax year rather than being duplicated here.
 */
export function detectColumnMapping(
  headers: string[],
  canonicalNames: string[],
  formType?: string | null,
  /**
   * What the customer said this file's columns mean, header to canonical name.
   *
   * Detection is three guesses at somebody else's spreadsheet, and it is right most of the
   * time and not all of the time. An override is not a guess, so it beats all three passes
   * and is never overruled by one.
   *
   * An explicit null means "this column is not used", which is a real answer and different
   * from having no answer: the column is then neither mapped nor reported as unplaced.
   * An override naming a column this form does not have is ignored rather than trusted -
   * it is stale, from a form type that has since changed.
   */
  overrides?: Record<string, string | null> | null
): MappingResult {
  const byExact = new Map<string, string>();
  const byShape = new Map<string, string>();
  for (const name of canonicalNames) {
    byExact.set(name, name);
    const shape = normalizeHeader(name);
    if (!byShape.has(shape)) byShape.set(shape, name);
  }

  const mapping: Record<string, string> = {};
  const unmapped: string[] = [];
  const claimed = new Set<string>();

  const take = (header: string, canonical: string): boolean => {
    if (claimed.has(canonical)) return false;
    mapping[header] = canonical;
    claimed.add(canonical);
    return true;
  };

  const known = new Set(canonicalNames);
  const overrideFor = (header: string): string | null | undefined => {
    if (!overrides) return undefined;
    const answer = Object.prototype.hasOwnProperty.call(overrides, header)
      ? overrides[header]
      : undefined;
    if (answer === undefined) return undefined;
    if (answer === null) return null;
    // recordRef is not one of the form's own columns but is always addressable.
    return known.has(answer) || answer === "recordRef" ? answer : undefined;
  };

  for (const header of headers) {
    const trimmed = header.trim();
    if (!trimmed) continue;

    // 0. what the customer said, which is not a guess and so outranks every guess below.
    const stated = overrideFor(trimmed);
    if (stated === null) continue;
    if (stated !== undefined && take(trimmed, stated)) continue;

    // 1. exact
    if (byExact.has(trimmed) && take(trimmed, trimmed)) continue;

    const shape = normalizeHeader(trimmed);
    if (!shape) {
      unmapped.push(header);
      continue;
    }

    // 2. alias table, before the shape match. "Recipient TIN" reduces to RECIPIENTTIN, and
    // the column list carries a legacy IRIS-shaped key spelled RecipientTIN that reduces the
    // same way; matched by shape first, the customer's TIN column landed on a field nothing
    // reads and the check reported the TIN missing. An alias is an explicit statement of
    // what a header means, so it outranks a coincidence of spelling.
    const candidates = ALIASES[shape];
    if (candidates) {
      const hit = candidates.find(
        (candidate) => byExact.has(candidate) || candidate === "recordRef"
      );
      if (hit && take(trimmed, hit)) continue;
    }

    // 3. shape-insensitive
    const shapeMatch = byShape.get(shape);
    if (shapeMatch && take(trimmed, shapeMatch)) continue;

    // 3b. box numbers, resolved against this form's own column list
    const box = /^BOX(\d+[A-Z]?)$/.exec(shape);
    if (box) {
      const hit = canonicalForBox(box[1], canonicalNames, claimed);
      if (hit && take(trimmed, hit)) continue;
    }

    // 3c. Pub 1220 amount codes: "Payment Amount 1", "Payment Amount A", "Amount Code A".
    //
    // Needs the form type, because the code-to-box translation is per form: amount code A
    // is box 10 on a 1099-MISC. Without a form type the header stays unmapped rather than
    // being read as a box number it may not be.
    const amount = /^(?:PAYMENT)?AMOUNT(?:CODE)?([0-9A-Z])$/.exec(shape);
    if (amount && formType) {
      const boxNumber = AMOUNT_CODE_BOX[formType]?.[amount[1]];
      if (boxNumber) {
        const hit = canonicalForBox(boxNumber, canonicalNames, claimed);
        if (hit && take(trimmed, hit)) continue;
      }
    }

    // 3d. the same box under another form's number. A 1099-MISC export calls golden parachute
    // pay BOX14_GOLDEN_PARACHUTE; on a 1099-NEC it is box 3, and BOX2_DIRECT_SALES_IND is
    // this form's BOX2_DIRECT_SALES with the word "indicator" on the end. The box number is
    // the part that differs between forms and years; the words after it are what the column
    // holds. Taken only when exactly one of this form's boxes has those words, so a header
    // that could mean two boxes still lands in front of the customer rather than in one of
    // them by luck.
    const tail = boxTail(trimmed);
    if (tail) {
      const hit = canonicalForBoxTail(tail, shape, canonicalNames, claimed);
      if (hit && take(trimmed, hit)) continue;

      // 3e. The box number, when the words after it match no box of this form. "Box 4
      // Federal Income Tax Withheld" and this form's BOX4_FED_INCOME_TAX_WITHHELD are the
      // same column written by two people; no spelling rule will ever cover every way a
      // payroll system writes a box's name, and the number in front of it is not a spelling,
      // it is the box. Taken only when this form has exactly one column under that number,
      // so a form whose box 5 is split across several columns still asks the customer.
      const sole = soleCanonicalForBox(boxNumber(trimmed), canonicalNames, claimed);
      if (sole && take(trimmed, sole)) continue;
    }

    unmapped.push(header);
  }

  return { mapping, unmapped };
}

/**
 * The words of a box column with the box number taken off, in comparable shape:
 * BOX14_GOLDEN_PARACHUTE -> GOLDENPARACHUTE, "Box 2 Direct Sales Ind" -> DIRECTSALE. Read from
 * the name as written, separator and all, because once a name is reduced to one run of
 * letters there is no telling BOX2 + DIRECT from BOX2D + IRECT. Null for a name that is not
 * a box or that is only a box number (those are pass 3b's).
 */
export function boxTail(name: string): string | null {
  const match = /^BOX[ _-]?(\d+[A-Za-z]?)[ _-]+(.+)$/i.exec(name.trim());
  if (!match) return null;
  const words = normalizeHeader(match[2]).replace(/(INDICATOR|IND)$/, "").replace(/S$/, "");
  return words || null;
}

/**
 * The one unclaimed canonical column under this box number, or null when there is a choice.
 *
 * Read from the number alone, so it is the last thing tried: the words are what usually
 * identify a column, and this is for the header whose words nothing recognises.
 */
function soleCanonicalForBox(
  box: string | null,
  canonicalNames: string[],
  claimed: Set<string>
): string | null {
  if (!box) return null;
  const hits = canonicalNames.filter((name) => !claimed.has(name) && boxNumber(name) === box);
  return hits.length === 1 ? hits[0] : null;
}

/** The box number of a box column, BOX14_GOLDEN_PARACHUTE -> 14, or null. */
function boxNumber(name: string): string | null {
  const match = /^BOX[ _-]?(\d+[A-Za-z]?)[ _-]+/i.exec(name.trim());
  return match ? match[1].toUpperCase() : null;
}

/**
 * The one unclaimed canonical box with these words after its number, or null.
 *
 * Two canonical names under the same box number are one box with two spellings - a 2025
 * BOX2_DIRECT_SALE beside its 2026 BOX2_DIRECT_SALES - and are not an ambiguity. Between
 * them, the spelling nearest the header wins, then the newer, longer one. Two different box
 * numbers with the same words are an ambiguity, and the header is left for the customer.
 */
function canonicalForBoxTail(
  tail: string,
  headerShape: string,
  canonicalNames: string[],
  claimed: Set<string>
): string | null {
  const hits = canonicalNames.filter((name) => !claimed.has(name) && boxTail(name) === tail);
  if (hits.length === 0) return null;
  const numbers = new Set(hits.map((name) => boxNumber(name)));
  if (numbers.size !== 1) return null;
  const bare = headerShape.replace(/(INDICATOR|IND)$/, "");
  return (
    hits.find((name) => normalizeHeader(name) === bare) ??
    [...hits].sort((a, b) => normalizeHeader(b).length - normalizeHeader(a).length)[0]
  );
}

/** Header names that can supply the stable record reference, most explicit first. */
const RECORD_REF_HEADERS = ["recordRef", "RECORD_REF", "ACCOUNT_NUMBER"];

/** The source header that supplies recordRef, or null when the rows need row-{n}. */
export function findRecordRefHeader(headers: string[]): string | null {
  for (const wanted of RECORD_REF_HEADERS) {
    const shape = normalizeHeader(wanted);
    const found = headers.find((header) => normalizeHeader(header) === shape);
    if (found) return found;
  }
  return null;
}

/**
 * Turns one spreadsheet row into a canonical record.
 *
 * Blank cells are dropped rather than sent as empty strings: an empty string in an IRS
 * money field is not the same as the field being absent, and the sanitizers treat them
 * differently.
 */
export function rowToRecord(
  row: Record<string, unknown>,
  mapping: Record<string, string>,
  recordRef: string
): Record<string, unknown> {
  const record: Record<string, unknown> = { recordRef };
  for (const [header, canonical] of Object.entries(mapping)) {
    if (canonical === "recordRef") continue;
    const value = row[header];
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    record[canonical] = typeof value === "string" ? value.trim() : value;
  }
  // The same fold the JSON body gets: a column the mapping landed on RECIPIENT_ZIP_CODE is
  // stored as the RECIPIENT_ZIP the generator reads, so an upload and a pasted body agree.
  return canonicalizeRecordKeys(record);
}

/**
 * Pulls the canonical column names out of the engine's JSON Schema for a form.
 *
 * `recordJsonSchema` calls `zodToJsonSchema` with a `name`, which makes the library wrap the
 * schema as `{ $ref: "#/definitions/<name>", definitions: { "<name>": { properties: {...} } } }`
 * rather than put `properties` at the root. Every canonical name would silently disappear
 * (every upload column reported unmapped, every CSV and XLSX e-file blocked with MISSING_FIELD
 * on every field) without resolving that indirection first.
 */
export function canonicalNamesFromJsonSchema(schema: Record<string, unknown>): string[] {
  const properties = propertiesOf(schema);
  return properties ? Object.keys(properties) : [];
}

/** `schema.properties`, or the `properties` of whatever `schema.$ref` points at in `definitions`/`$defs`. */
function propertiesOf(schema: Record<string, unknown>): Record<string, unknown> | null {
  const direct = schema.properties;
  if (direct && typeof direct === "object") return direct as Record<string, unknown>;

  const ref = schema.$ref;
  if (typeof ref !== "string") return null;
  // "#/definitions/Foo" or "#/$defs/Foo" -> ["definitions", "Foo"] / ["$defs", "Foo"].
  const match = /^#\/(definitions|\$defs)\/([^/]+)$/.exec(ref);
  if (!match) return null;
  const [, bucketName, key] = match;
  const bucket = schema[bucketName];
  if (!bucket || typeof bucket !== "object") return null;
  const target = (bucket as Record<string, unknown>)[decodeURIComponent(key)];
  if (!target || typeof target !== "object") return null;
  const targetProperties = (target as Record<string, unknown>).properties;
  return targetProperties && typeof targetProperties === "object"
    ? (targetProperties as Record<string, unknown>)
    : null;
}
